import {KeyValuePair} from "../Global/KeyValuePair";
import CaptchaModel from "./CaptchaModel";
import CaptchaTokenManager from "./CaptchaTokenManager";
import {CaptchaType} from "./CaptchaType";
import DNTcaptchaInitializer from "./DNTcaptchaInitializer";
import RecaptchaV2Initializer from "./RecaptchaV2Initializer";
import RecaptchaV3Initializer from "./RecaptchaV3Initializer";

declare const captchaType: CaptchaType;

export default class CaptchaInitializer {
    private readonly dntCaptchaInitializer: DNTcaptchaInitializer;
    private readonly recaptchaV3Initializer: RecaptchaV3Initializer;
    private readonly recaptchaV2Initializer: RecaptchaV2Initializer;
    private readonly mCaptchaTokenManager: CaptchaTokenManager;

    public get captchaTokenManager(): CaptchaTokenManager {
        return this.mCaptchaTokenManager;
    }

    constructor(captchaTokenManager: CaptchaTokenManager) {
        this.mCaptchaTokenManager = captchaTokenManager;

        this.dntCaptchaInitializer = new DNTcaptchaInitializer();
        this.recaptchaV3Initializer = new RecaptchaV3Initializer();
        this.recaptchaV2Initializer = new RecaptchaV2Initializer();
    }

    public prepareContainer(jq: JQueryStatic, container: JQuery): void {
        if (typeof captchaType === "undefined") {
            return;
        }
        if (captchaType === CaptchaType.DNTCaptcha) {
            this.dntCaptchaInitializer.prepareContainer(jq, container, this.mCaptchaTokenManager);
        }
        if (captchaType === CaptchaType.RecaptchaV2) {
            this.recaptchaV2Initializer.prepareContainer(jq, container);
        }
        if (captchaType === CaptchaType.RecaptchaV3) {
            this.recaptchaV3Initializer.prepareTextContainer(jq, container);
        }
    }

    public cleanupContainer(container: JQuery): void {
        if (typeof captchaType === "undefined") {
            return;
        }
        if (captchaType === CaptchaType.DNTCaptcha) {
            this.dntCaptchaInitializer.cleanupContainer(container);
        }
    }

    private init(jq: JQueryStatic, container: JQuery, model: CaptchaModel): void {
        if (captchaType === CaptchaType.RecaptchaV3) {
            RecaptchaV3Initializer.init(jq, model.RecaptchaV3Model.Action, model.RecaptchaV3Model.Callback);
        }
        if (captchaType === CaptchaType.DNTCaptcha) {
            this.dntCaptchaInitializer.init(jq, container, model.DNTModel.Callback);
        }
        if (captchaType === CaptchaType.RecaptchaV2) {
            this.recaptchaV2Initializer.init(jq, model.RecaptchaV2Model.Callback);
        }
    }

    public initForAjax(
        jq: JQueryStatic,
        container: JQuery,
        captchaCallback: (captchaParams: Array<KeyValuePair<string, string>>) => void,
        recaptchaAction?: string,
    ): void {
        this.checkCaptchaAction(recaptchaAction);
        if (typeof captchaType === "undefined") {
            captchaCallback(null);
            return;
        }
        const captchaParams: Array<KeyValuePair<string, string>> = [];
        const captchaModel: CaptchaModel = {
            RecaptchaV3Model: {
                Action: recaptchaAction,
                Callback: (token) => {
                    captchaParams.push(["recaptchaV3", token]);
                    captchaCallback(captchaParams);
                },
            },
            RecaptchaV2Model: {
                Callback: (response) => {
                    captchaParams.push(["recaptchaV2", response]);
                    captchaCallback(captchaParams);
                },
            },
            DNTModel: {
                Callback: (enteredValue) => {
                    enteredValue.each((i, el) => {
                        captchaParams.push([el.name, el.value]);
                    });
                    captchaCallback(captchaParams);
                },
            },
        };
        const captchaToken = this.mCaptchaTokenManager.getTokenParam();
        if (captchaToken) {
            captchaParams.push(captchaToken);
            captchaCallback(captchaParams);
        } else {
            this.init(jq, container, captchaModel);
        }
    }

    public static setCaptchaHeaders(captchaParams: Array<KeyValuePair<string, string>>): JQuery.PlainObject<string> {
        const result = {};
        if (captchaParams !== null) {
            for (const captchaParam of captchaParams) {
                result[`X-${captchaParam[0]}`] = captchaParam[1];
            }
        }
        return result;
    }

    public initForForm(jq: JQueryStatic, form: JQuery<HTMLFormElement>, recaptchaAction: string, submitButton: JQuery = null): void {
        this.checkCaptchaAction(recaptchaAction);
        if (typeof captchaType === "undefined") {
            form[0].submit();
            return;
        }

        const captchaModel: CaptchaModel = {
            RecaptchaV3Model: {
                Action: recaptchaAction,
                Callback: (token) => {
                    form.append(`<input type="hidden" name="recaptchaV3" value=${token} />`);
                    form[0].submit();
                },
            },
            RecaptchaV2Model: {
                Callback: (reponse) => {
                    form.append(`<input type="hidden" name="recaptchaV2" value=${reponse} />`);
                    form[0].submit();
                },
            },
            DNTModel: {
                Callback: (enteredValue) => {
                    if (submitButton) {
                        submitButton.prop("disabled", true);
                    }
                    form.append(enteredValue);
                    form[0].submit();
                },
            },
        };
        const captchaToken = this.mCaptchaTokenManager.getTokenParam();
        if (captchaToken) {
            form.append(`<input type="hidden" name="${captchaToken[0]}" value=${captchaToken[1]} />`);
            form[0].submit();
        } else {
            this.init(jq, form, captchaModel);
        }
    }

    private checkCaptchaAction(recaptchaAction: string): boolean {
        if (!/^[\w\d_/]*$/g.test(recaptchaAction)) {
            throw Error("Actions might contain only alphanumeric characters, slashes, and underscores");
        }

        return true;
    }
}
